import { AbstractTextContentVO, HTMLTextVO } from "@goodgamestudios/localization";
import { GGSCountryController } from "../../language/countryMapper/GGSCountryController";
import { debug } from "../../logging/debug";
import { warn } from "../../logging/warn";
import { FontConfigVO } from "../FontConfigVO";
import { ChangeSignal } from "./signals/ChangeSignal";
import { ClickSignal } from "./signals/ClickSignal";
import { FocusInSignal } from "./signals/FocusInSignal";
import { FocusOutSignal } from "./signals/FocusOutSignal";
import { HTMLLinkClickSignal } from "./signals/HTMLLinkClickSignal";
import { KeyDownSignal } from "./signals/KeyDownSignal";
import { KeyUpSignal } from "./signals/KeyUpSignal";
import { MouseOutSignal } from "./signals/MouseOutSignal";
import { MouseOverSignal } from "./signals/MouseOverSignal";
import { RemovedFromStageSignal } from "./signals/RemovedFromStageSignal";
import { ScrollSignal } from "./signals/ScrollSignal";
import { TextInputSignal } from "./signals/TextInputSignal";
import { IInternalGGSTextField } from "./IInternalGGSTextField";
import { IBasicGGSTextField } from "./IBasicGGSTextField";
import { ITextFieldStrategy } from "./ITextFieldStrategy";
import { GGSTextFieldModel } from "./GGSTextFieldModel";
import { getLogger } from "@goodgamestudios/logger";
// import { GGSTextFieldTLFStrategy } from "./GGSTextFieldTLFStrategy";
import { GGSTextFieldDefaultStrategy } from "./GGSTextFieldDefaultStrategy";
import { ColorTransform, TextField, TextFieldAutoSize } from "@goodgamestudios/createjs-utilities";
import Rectangle = createjs.Rectangle;

const logger = getLogger("GoodgameTextField");
export class GoodgameTextField implements IInternalGGSTextField, IBasicGGSTextField {
    public static NAME: string = "GoodgameTextField";

    protected _originalTextField: TextField;

    protected _availableStrategies: any[] = [];
    protected _textStrategy: ITextFieldStrategy;
    protected _textContentVO: AbstractTextContentVO;

    private _htmlLinkClickSignal: HTMLLinkClickSignal;
    private _mouseOutSignal: MouseOutSignal;
    private _mouseOverSignal: MouseOverSignal;
    private _keyDownSignal: KeyDownSignal;
    private _keyUpSignal: KeyUpSignal;
    protected _focusOutSignal: FocusOutSignal;
    protected _focusInSignal: FocusInSignal;
    private _clickSignal: ClickSignal;
    private _textInputSignal: TextInputSignal;
    private _changeSignal: ChangeSignal;
    private _removedFromStageSignal: RemovedFromStageSignal;
    private _scrollSignal: ScrollSignal;

    protected _model: GGSTextFieldModel;
    private _loadedFontConfig: FontConfigVO;
    private _originalFontConfig: FontConfigVO;


    private isFontConfigInitiallyApplied: boolean = false;

    constructor(textField: TextField) {
        this._originalTextField = textField;

        // Create original font config
        this._originalFontConfig = new FontConfigVO();
        this._originalFontConfig.initalize(this._originalTextField);

        // Initialize model
        this._model = new GGSTextFieldModel(this);

        // Initialize default strategy
        this.useDefaultStrategy();
    }

    //TODO @HTML5 implement this method
    public useTLFStrategy(): void {
        logger.debug("useTLFStrategy not implemented");
        //     if (!this._availableStrategies[1]) {
        //         this._availableStrategies[1] = new GGSTextFieldTLFStrategy(this);
        //     }
        //
        //     //			trace("useTLFStrategy: " + useTLFStrategy, _availableStrategies[1]);
        //
        //     this.activateStrategy(this._availableStrategies[1]);
    }

    public useDefaultStrategy(): void {
        logger.debug("useDefaultStrategy ");
        if (!this._availableStrategies[0]) {
            this._availableStrategies[0] = new GGSTextFieldDefaultStrategy(this);
        }

        this.activateStrategy(this._availableStrategies[0]);
    }

    protected activateStrategy(newStrategy: ITextFieldStrategy): void {
        //logger.debug("activateStrategy ", this, newStrategy);
        if (newStrategy == this._textStrategy) {
            // Strategy hasn't changed. Save the work.
            return;
        }

        if (this._textStrategy) {
            this._textStrategy.deactivate();
        }

        // Set new strategy
        this._textStrategy = newStrategy;

        //			trace("GoodgameTextField, activateStrategy() -> " + _textStrategy);

        // Activate new strategy
        this._textStrategy.activate();

        this.updateStrategyListeners();
    }

    // Hook method
    protected updateStrategyListeners(): void {
    }

    private onSignalRemoved(eventType: string): void {
        if (this._textStrategy && this._textStrategy.hasEventListenerForEventType(eventType)) {
            //				trace(NAME + ", onSignalRemoved: " + eventType);
            this._model.removeRegisteredEventListener(eventType);
            this._textStrategy.removeEventListenerForEventType(eventType);
        }
    }

    private onSignalAdded(eventType: string): void {
        if (!this._textStrategy.hasEventListenerForEventType(eventType)) {
            //				trace(NAME + ", onSignalAdded: " + eventType);
            this._model.addRegisteredEventListener(eventType);
            this._textStrategy.addEventListenerForEventType(eventType);
        }
    }

    public get click(): ClickSignal {
        if (!this._clickSignal) {
            this._clickSignal = new ClickSignal();
            this._clickSignal.removed = this.bindFunction(this.onSignalRemoved);
            this._clickSignal.added = this.bindFunction(this.onSignalAdded);
        }

        return this._clickSignal;
    }

    public get keyDown(): KeyDownSignal {
        if (!this._keyDownSignal) {
            this._keyDownSignal = new KeyDownSignal();
            this._keyDownSignal.removed = this.bindFunction(this.onSignalRemoved);
            this._keyDownSignal.added = this.bindFunction(this.onSignalAdded);
        }

        return this._keyDownSignal;
    }

    public get keyUp(): KeyUpSignal {
        if (!this._keyUpSignal) {
            this._keyUpSignal = new KeyUpSignal();
            this._keyUpSignal.removed = this.bindFunction(this.onSignalRemoved);
            this._keyUpSignal.added = this.bindFunction(this.onSignalAdded);
        }

        return this._keyUpSignal;
    }

    public get mouseOver(): MouseOverSignal {
        if (!this._mouseOverSignal) {
            this._mouseOverSignal = new MouseOverSignal();
            this._mouseOverSignal.removed = this.bindFunction(this.onSignalRemoved);
            this._mouseOverSignal.added = this.bindFunction(this.onSignalAdded);
        }

        return this._mouseOverSignal;
    }

    public get mouseOut(): MouseOutSignal {
        if (!this._mouseOutSignal) {
            this._mouseOutSignal = new MouseOutSignal();
            this._mouseOutSignal.removed = this.bindFunction(this.onSignalRemoved);
            this._mouseOutSignal.added = this.bindFunction(this.onSignalAdded);
        }

        return this._mouseOutSignal;
    }

    public get focusIn(): FocusInSignal {
        if (!this._focusInSignal) {
            this._focusInSignal = new FocusInSignal();
            this._focusInSignal.removed = this.bindFunction(this.onSignalRemoved);
            this._focusInSignal.added = this.bindFunction(this.onSignalAdded);
        }

        return this._focusInSignal;
    }

    public get focusOut(): FocusOutSignal {
        if (!this._focusOutSignal) {
            this._focusOutSignal = new FocusOutSignal();
            this._focusOutSignal.removed = this.bindFunction(this.onSignalRemoved) as Function;
            this._focusOutSignal.added = this.bindFunction(this.onSignalAdded);
        }

        return this._focusOutSignal;
    }

    public get htmlLinkClick(): HTMLLinkClickSignal {
        if (!this._htmlLinkClickSignal) {
            this._htmlLinkClickSignal = new HTMLLinkClickSignal();
            this._htmlLinkClickSignal.removed = this.bindFunction(this.onSignalRemoved);
            this._htmlLinkClickSignal.added = this.bindFunction(this.onSignalAdded);
        }

        return this._htmlLinkClickSignal;
    }

    public get textInput(): TextInputSignal {
        if (!this._textInputSignal) {
            this._textInputSignal = new TextInputSignal();
            this._textInputSignal.removed = this.bindFunction(this.onSignalRemoved);
            this._textInputSignal.added = this.bindFunction(this.onSignalAdded);
        }

        return this._textInputSignal;
    }

    public get change(): ChangeSignal {
        if (!this._changeSignal) {
            this._changeSignal = new ChangeSignal();
            this._changeSignal.removed = this.bindFunction(this.onSignalRemoved);
            this._changeSignal.added = this.bindFunction(this.onSignalAdded);
        }

        return this._changeSignal;
    }

    public get removedFromStage(): RemovedFromStageSignal {
        if (!this._removedFromStageSignal) {
            this._removedFromStageSignal = new RemovedFromStageSignal();
            this._removedFromStageSignal.removed = this.bindFunction(this.onSignalRemoved);
            this._removedFromStageSignal.added = this.bindFunction(this.onSignalAdded);
        }

        return this._removedFromStageSignal;
    }

    public get scroll(): ScrollSignal {
        if (!this._scrollSignal) {
            this._scrollSignal = new ScrollSignal();
            this._scrollSignal.removed = this.bindFunction(this.onSignalRemoved);
            this._scrollSignal.added = this.bindFunction(this.onSignalAdded);
        }

        return this._scrollSignal;
    }

    public displayText(): void {
        // HTML text (doesn't support maxChars restriction)
        if (this._textContentVO instanceof HTMLTextVO) {
            this._textStrategy.displayText(this._model.text, this._textContentVO);
            return;
        }

        let textToDisplay: string = this._model.text;

        // Cut text if maxChars is set
        if (!isNaN(this._model.maxChars) && this._model.maxChars > 0) {
            textToDisplay = this._model.text.slice(0, this._model.maxChars);
        }

        // Null check
        if (textToDisplay === null) {
            // Prevent null pointer
            textToDisplay = "";
            this.printOutDebugInfos();
        }

        this._textStrategy.displayText(textToDisplay);
    }

    protected printOutDebugInfos(): void {
        // Trace some debug infos
        if (this._originalTextField) {
            let debugTextFieldName: string = this._originalTextField.name;
            let debugTextFieldParentName: string = (this._originalTextField.parent) ? (this._originalTextField.parent.name + ".") : "";
            let debugTextFieldNameWithParentName: string = debugTextFieldParentName + debugTextFieldName;
            let debugTextContentVOInfo: string = (this.textContentVO) ? (", textContendVO: " + this.textContentVO.toString()) : "";
            warn("Text is null. TextField:" + debugTextFieldNameWithParentName + debugTextContentVOInfo);
        }
    }

    /**
     * Resolves the final text content String according to the specific <code>AbstractTextContentVO</code> and saves it in the model.
     * Some TextContentVOs might trigger a localization like the LocalizedTextVO or LocalizedNumberVO.
     */
    protected resolveTextContent(textContentVO: AbstractTextContentVO): void {
        this._model.text = textContentVO.compose();
    }

    public applyFontConfiguration(config: FontConfigVO): void {
        this._textStrategy.applyFontConfiguration(config);
    }

    public set color(color: string | number) {
        this._model.color = ColorTransform.colorNumToString(color);
    }

    public get color(): string | number {
        return this._model.color;
    }

    public get originalTextField(): TextField {
        return this._originalTextField;
    }

    public unregister(): void {
        // Apply original properties of the _originalTextField. That is needed if the original TextField won't be destroyed
        // by a relog-in.
        if (this.currentFontConfig == this._loadedFontConfig) {
            this.applyFontConfiguration(this._originalFontConfig);

            this._originalTextField.y = this._originalFontConfig.yPosOriginal;
        }

        // Remove signals (They'll unregister the corresponding events automatically in the strategies
        // before the strategies will be disposed, too.
        if (this._clickSignal) {
            this._clickSignal.removeAll();
            this._clickSignal = null;
        }

        if (this._focusInSignal) {
            this._focusInSignal.removeAll();
            this._focusInSignal = null;
        }

        if (this._focusOutSignal) {
            this._focusOutSignal.removeAll();
            this._focusOutSignal = null;
        }

        if (this._keyDownSignal) {
            this._keyDownSignal.removeAll();
            this._keyDownSignal = null;
        }

        if (this._mouseOverSignal) {
            this._mouseOverSignal.removeAll();
            this._mouseOverSignal = null;
        }

        if (this._mouseOutSignal) {
            this._mouseOutSignal.removeAll();
            this._mouseOutSignal = null;
        }

        if (this._htmlLinkClickSignal) {
            this._htmlLinkClickSignal.removeAll();
            this._htmlLinkClickSignal = null;
        }

        if (this._textInputSignal) {
            this._textInputSignal.removeAll();
            this._textInputSignal = null;
        }

        if (this._scrollSignal) {
            this._scrollSignal.removeAll();
            this._scrollSignal = null;
        }

        // Dispose strategies
        for (let strategy of this._availableStrategies) {
            strategy.dispose();
            strategy = null;
        }
        this._availableStrategies = null;
        this._textStrategy = null;

        // restore original textfield visibility state
        this._originalTextField.visible = this._originalFontConfig.visible;

        // Null all references
        this._originalTextField = null;

        // Dispose font configs
        this._originalFontConfig.dispose();
        this._originalFontConfig = null;

        if (this._loadedFontConfig) {
            this._loadedFontConfig.dispose();
            this._loadedFontConfig = null;
        }

        if (this._textContentVO) {
            this._textContentVO.dispose();
            this._textContentVO = null;
        }

        // Dispose model
        this._model.dispose();
        this._model = null;
    }

    public get originalFontConfig(): FontConfigVO {
        return this._originalFontConfig;
    }

    public get loadedFontConfig(): FontConfigVO {
        return (<FontConfigVO>this._loadedFontConfig);
    }

    public set loadedFontConfig(value: FontConfigVO) {
        this._loadedFontConfig = value;
    }

    public set autoSize(value: TextFieldAutoSize) {
        this._model.autoSize = value;
    }

    public get uid(): number {
        return this._model.uid;
    }

    public get x(): number {
        return this._model.x;
    }

    public set x(value: number) {
        this._model.x = value;
    }

    public get y(): number {
        return this._model.y;
    }

    public set y(value: number) {
        this._model.y = value;
    }

    public get width(): number {
        return this._model.width;
    }

    public set width(value: number) {
        this._model.width = value;
    }

    public get height(): number {
        return this._model.height;
    }

    public set height(value: number) {
        this._model.height = value;
    }

    /**
     * @param text
     */
    public set text(text: string) {
        this._model.text = text;
        this.displayText();
    }

    public get text(): string {
        return this._model.text;
    }

    public get currentFontConfig(): FontConfigVO {
        return this._textStrategy.currentFontConfig;
    }

    public set data(value: any) {
        this._model.data = value;
    }

    public get data(): any {
        return this._model.data;
    }

    public setFocus(): void {
        this._textStrategy.setFocus();
    }

    public set tabIndex(index: number) {
        this._model.tabIndex = index;
    }

    public get tabIndex(): number {
        return this._model.tabIndex;
    }

    public set tabEnabled(flag: boolean) {
        this._model.tabEnabled = flag;
    }

    public get tabEnabled(): boolean {
        return this._model.tabEnabled;
    }

    public set maxChars(value: number) {
        this._model.maxChars = value;
        this.displayText();
    }

    public get textContentVO(): AbstractTextContentVO {
        if (!this._textContentVO) {
            if (this._originalTextField) {
                debug(" get textContentVO: VO is null. " + (this._originalTextField) ? this._originalTextField.name : "");
            }
            else {
                debug(" get textContentVO: VO is null. (_originalTextField is null)");
            }
            return null;
        }

        this._textContentVO.propertyChangedSignal.add(this.bindFunction(this.onTextContentVOChanged));

        return this._textContentVO;
    }

    public set textContentVO(vo: AbstractTextContentVO) {
        this._textContentVO = vo;

        if (!this._textContentVO.triggerUpdate) {
            return;
        }

        this.displayLocalizedText(GGSCountryController.instance.currentCountry.ggsLanguageCode, this._textContentVO);
    }

    protected onTextContentVOChanged(): void {
        if (!this._textContentVO.triggerUpdate) {
            return;
        }

        this.displayLocalizedText(GGSCountryController.instance.currentCountry.ggsLanguageCode, this._textContentVO);
    }


    // TODO: naming: updateTextComposition() ?
    public displayLocalizedText(ggsLanguageCode: string, textContentVO: AbstractTextContentVO = null): void {

        // Store textContentVO if defined
        if (textContentVO) {
            this._textContentVO = textContentVO;
        }

        // Check if localization is ready to be executed. That's because
        if (!this._textContentVO.triggerUpdate) {
            return;
        }

        // Resolve text content with textContentVO
        this.resolveTextContent(textContentVO);

        // Retrieve the needed font configuration (includes the needed font) depending on the current language


        // let fontConfig: FontConfigVO = this._fontManager.retrieveFontConfiguration(this, ggsLanguageCode);
        let fontConfig: FontConfigVO = this._originalFontConfig;
        // TODO  HTML5   here we might still need some special config for LeftToRight languages
        //			trace(NAME + " displayLocalizedText -> FONT CONFIG VO: " + fontConfig);

        let fontConfigOrStrategyChanged: boolean;

        // Update font config and strategy only if font config has changed (saves especially for TLF strategy much performance!)
        // TODO HTML5 HTML-2128 here we still have valid info (color and size) in the TextFormat of the fontConfig
        if (fontConfig !== this.textStrategy.currentFontConfig || !this.isFontConfigInitiallyApplied || this.autoFitToBounds) {
            fontConfigOrStrategyChanged = true;
            this.isFontConfigInitiallyApplied = true;

            // Set appendText to false to force the HTML to be recreated and its content to be
            // newly localized for the just happened strategy switch
            if (textContentVO instanceof HTMLTextVO) {
                ((<HTMLTextVO>textContentVO)).appendText = false;
            }

            // Decide which text rendering strategy is needed for the current TextField regarding the current language
            this.chooseTextRenderingStrategy(ggsLanguageCode, fontConfig);

            // Apply FontConfigVO with current strategy
            this.applyFontConfiguration(fontConfig);
        }

        // For verticalAlign-function is it necessary to set the text before updating the properties.
        // Attention: this permutation ("displayText() --> updateProperties()" instead of "updateProperties() --> displayText()") can cause errors!

        // Display text
        this.displayText();

        // Update all properties after changing font configuration (font configuration changes implicit
        // when strategy changes)
        this.updateProperties(fontConfigOrStrategyChanged);
    }

    public updateProperties(forceUpdate: boolean = false): void {

        // Update properties initially or if forced
        if (!this._model.initialized || forceUpdate) {

            this._model.updateAllProperties(true);
            this._model.initialized = true;
        }
    }

    /**
     * Defines criteria when to use a specific strategy to render the font.
     * @param textField
     * @param currentGGSLanguageCode
     */
    protected chooseTextRenderingStrategy(currentGGSLanguageCode: string, fontConfig: FontConfigVO): void {
        // TLF is Flash specific and is not going to be supported in the browser

        // // TODO: Create GGSLanguageCodes analog to GGSCountryCodes
        // if (currentGGSLanguageCode == "ar" && (fontConfig instanceof RuntimeLoadedFontConfigVO)) {
        //     //				trace(NAME + ", STRATEGY: TLF -> " + currentGGSLanguageCode);
        //
        //     // Use TLF strategy
        //     this.useTLFStrategy();
        // }
        // else {
        //     //				trace(NAME + ", STRATEGY: Default -> " + currentGGSLanguageCode);
        //
        //     this.useDefaultStrategy();
        // }

        this.useDefaultStrategy();
    }

    public get multiline(): boolean {
        return this._model.multiline;
    }

    public set multiline(value: boolean) {
        this._model.multiline = value;
    }

    public get restrict(): string {
        return this._model.restrict;
    }

    public set restrict(value: string) {
        this._model.restrict = value;
    }

    public get wordwrap(): boolean {
        return this._model.wordwrap;
    }

    public set wordwrap(value: boolean) {
        this._model.wordwrap = value;
    }

    public get textWidth(): number {
        return this._textStrategy.textWidth;
    }

    public get textHeight(): number {
        return this._textStrategy.textHeight;
    }

    public get caretIndex(): number {
        return this._textStrategy.caretIndex;
    }

    public set mouseEnabled(value: boolean) {
        this._model.mouseEnabled = value;
    }

    public get mouseEnabled(): boolean {
        return this._model.mouseEnabled;
    }

    public set verticalAlign(direction: string) {
        this._model.verticalAlign = direction;
    }

    public set textAlign(direction: string) {
        this._model.textAlign = direction;
    }

    public setSelection(beginIndex: number, endIndex: number): void {
        this._textStrategy.setSelection(beginIndex, endIndex);
    }

    public get selectable(): boolean {
        return this._model.selectable;
    }

    public set selectable(value: boolean) {
        this._model.selectable = value;
    }

    public set type(type: string) {
        this._model.type = type;
    }

    public get type(): string {
        return this._model.type;
    }

    public get size(): number {
        return this._model.size;
    }

    public set size(value: number) {
        this._model.size = value;
    }

    public get model(): GGSTextFieldModel {
        return this._model;
    }

    public get visible(): boolean {
        return this._model.visible;
    }

    public set visible(value: boolean) {
        this._model.visible = value;
    }

    public get scaleX(): number {
        return this._model.scaleX;
    }

    public set scaleX(value: number) {
        this._model.scaleX = value;
    }

    public get scaleY(): number {
        return this._model.scaleY;
    }

    public set scaleY(value: number) {
        this._model.scaleY = value;
    }

    public get rotation(): number {
        return this._model.rotation;
    }

    public set rotation(value: number) {
        this._model.rotation = value;
    }

    public get background(): boolean {
        return this._model.background;
    }

    public set background(value: boolean) {
        this._model.background = value;
    }

    public get backgroundColor(): string {
        return this._model.backgroundColor;
    }

    public set backgroundColor(value: string) {
        this._model.backgroundColor = value;
    }

    public fitTextSizeToBounds(bounds: Rectangle = null): void {
        this._textStrategy.fitTextSizeToBounds(bounds);
    }

    public get numLines(): number {
        return this._textStrategy.numLines;
    }

    /**
     * Clears displayed text, but not the textContentVO.
     * To restore the text content call "restoreTextContent()"
     */
    public clearText(): void {
        //_textStrategy.displayText("");
        this.text = "";
    }

    public restoreTextContent(): void {
        this.displayText();
    }

    public set indent(value: number) {
        this._model.indent = value;
    }

    public get textStrategy(): ITextFieldStrategy {
        return this._textStrategy;
    }

    public set autoFitToBounds(value: boolean) {
        this._model.autoFitToBounds = value;
    }

    public get autoFitToBounds(): boolean {
        return this._model.autoFitToBounds;
    }

    public set scrollV(value: number) {
        this._model.scrollV = value;
    }

    public get scrollV(): number {
        return this._model.scrollV;
    }

    public get maxScrollV(): number {
        return this.textStrategy.maxScrollV;
    }
}
